import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tag, Spin, Tabs } from "antd";

import { all_routes } from "../../../router/all_routes";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { fetchJobDetails } from "../../../../core/data/redux/recruitment/thunks";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import DragDropRoundBuilder from "../components/DragDropRoundBuilder";



const getGradient = (seed: string = "") => {
  const colors = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4481eb 0%, #04befe 100%)",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const formatSalary = (val: number | null | undefined) => {
  if (val == null) return null;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${val.toLocaleString("en-IN")}`;
};

const toTitleCase = (str: string = "") =>
  str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());



interface SectionPanelProps {
  id: string;
  icon: string;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  accentColor?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SectionPanel: React.FC<SectionPanelProps> = ({
  id, icon, title, subtitle, badge,
  accentColor = "#4f46e5", defaultOpen = true, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className="section-panel"
      style={{
        background: "#fff",
        border: "1px solid #e8eaf0",
        borderRadius: 14,
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        marginBottom: 16,
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`section-body-${id}`}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 14,
          padding: "14px 20px", background: open ? "#fafbff" : "#fff",
          border: "none", borderBottom: open ? "1px solid #eef0f8" : "none",
          cursor: "pointer", textAlign: "left", transition: "background 0.18s",
        }}
      >
        <div style={{ width: 3, height: 32, borderRadius: 99, background: accentColor, flexShrink: 0, opacity: open ? 1 : 0.35, transition: "opacity 0.2s" }} />
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${accentColor}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className={`ti ${icon}`} style={{ fontSize: 18, color: accentColor }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.2px" }}>{title}</span>
            {badge}
          </div>
          {subtitle && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>{subtitle}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 20, background: open ? `${accentColor}12` : "#f1f5f9", transition: "background 0.18s", flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: open ? accentColor : "#94a3b8", transition: "color 0.18s" }}>{open ? "Collapse" : "Expand"}</span>
          <i className="ti ti-chevron-up" style={{ fontSize: 14, color: open ? accentColor : "#94a3b8", transform: open ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.25s ease, color 0.18s", display: "block" }} />
        </div>
      </button>
      <div id={`section-body-${id}`} style={{ display: open ? "block" : "none" }}>{children}</div>
      <style>{`.section-panel:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important; }`}</style>
    </div>
  );
};



const StatTile = ({ icon, label, value, accent }: { icon: string; label: string; value: React.ReactNode; accent: string }) => (
  <div style={{ padding: "14px 20px", display: "flex", alignItems: "flex-start", gap: 10, minWidth: 140, borderRight: "1px solid #f1f5f9", flexShrink: 0 }}>
    <div style={{ width: 34, height: 34, borderRadius: 9, background: `${accent}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 15, color: accent }} />
    </div>
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", wordBreak: "break-word" }}>{value}</div>
    </div>
  </div>
);



const DetailRow = ({ icon, label, value, accent = "#64748b" }: { icon: string; label: string; value: React.ReactNode; accent?: string }) => (
  <div className="d-flex align-items-start gap-3">
    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${accent}12`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 14, color: accent }} />
    </div>
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: "#334155", marginTop: 2 }}>{value || "—"}</div>
    </div>
  </div>
);



const SkillBadge = ({ skill, type = "required" }: { skill: string; type?: "required" | "preferred" }) =>
  type === "required" ? (
    <span className="rounded-pill" style={{ fontSize: 11, padding: "3px 10px", background: "#f0f4ff", color: "#4f46e5", border: "1px solid #dde3ff", fontWeight: 600 }}>{skill}</span>
  ) : (
    <span className="rounded-pill" style={{ fontSize: 11, padding: "3px 10px", background: "#fdf4ff", color: "#c026d3", border: "1px solid #fce7f3", fontWeight: 600 }}>{skill}</span>
  );



const JobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);
  const job = useSelector((state: any) => state.recruitment.activeJob);
  const loading = useSelector((state: any) => state.recruitment.activeJobLoading);

  useEffect(() => {
    if (id && activeCompany?.company_id) {
      dispatch(fetchJobDetails(Number(id)));
    }
  }, [dispatch, id, activeCompany?.company_id]);

  

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
          <div className="text-center">
            <Spin size="large" />
            <p className="text-muted mt-3" style={{ fontSize: 14 }}>Loading job details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <i className="ti ti-briefcase-off d-block mb-3 opacity-50" style={{ fontSize: 48 }} />
          <h5 className="fw-bold">Job not found</h5>
          <Link to={all_routes.jobgrid} className="btn btn-primary rounded-pill px-4 mt-2">
            Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  

  const statusColorMap: Record<string, string> = {
    active: "#10b981",
    draft: "#f59e0b",
    closed: "#ef4444",
    on_hold: "#64748b",
  };
  const statusColor = statusColorMap[job.status] || "#3b82f6";

  const salaryDisplay = (() => {
    if (!job.display_salary) return "Not Disclosed";
    if (job.min_salary && job.max_salary)
      return `${formatSalary(job.min_salary)} – ${formatSalary(job.max_salary)}`;
    if (job.salary_range) {
      if (typeof job.salary_range === "object") {
        const sr = job.salary_range as any;
        return `${sr.currency || ""} ${sr.min || ""} - ${sr.max || ""}`.trim();
      }
      return String(job.salary_range);
    }
    return "Not Disclosed";
  })();

  const screeningQuestions: any[] = job.screening_questions || [];
  const platforms: any[] = job.platforms || [];
  const requiredSkills: string[] = job.required_skills || [];
  const preferredSkills: string[] = job.preferred_skills || [];

  const isDeadlinePassed = job.application_deadline && new Date(job.application_deadline) < new Date();
  const daysUntilDeadline = job.application_deadline
    ? Math.ceil((new Date(job.application_deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="page-wrapper">
      <div className="content" style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }}>

        
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Job Details</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}><i className="ti ti-smart-home" /></Link>
                </li>
                <li className="breadcrumb-item">Recruitment</li>
                <li className="breadcrumb-item active" aria-current="page">Job</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <div className="mb-2">
              <Link to={all_routes.jobgrid} className="btn btn-info d-flex align-items-center">
                <i className="ti ti-arrow-back me-1" />Go Back
              </Link>
            </div>
            <div className="head-icons"><CollapseHeader /></div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          
          <SectionPanel
            id="overview"
            icon="ti-briefcase"
            title="Job Overview"
            subtitle={`${job.job_code || "N/A"} · ${job.department || "Organization"}`}
            accentColor="#4f46e5"
            badge={
              <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 600, backgroundColor: `${statusColor}1A`, color: statusColor, border: `1px solid ${statusColor}40` }}>
                <i className="ti ti-point-filled" style={{ fontSize: 8, marginRight: 4 }} />
                {job.status ? job.status.replace(/_/g, " ").toUpperCase() : ""}
              </span>
            }
            defaultOpen
          >
            <div>
              
              <div style={{ height: 3, background: getGradient(job.job_title) }} />

              <div className="p-4 pb-3">
                
                <div className="d-flex align-items-center gap-0 flex-wrap">

                  
                  <div className="d-flex align-items-center gap-3 flex-shrink-0" style={{ minWidth: 280, paddingRight: 20, borderRight: "1px solid #f0f0f8" }}>
                    <div
                      className="d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                      style={{ width: 64, height: 64, fontSize: 20, borderRadius: 14, background: getGradient(job.job_title), letterSpacing: "-1px" }}
                    >
                      {job.job_title?.substring(0, 2).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h4 className="fw-bold mb-0" style={{ fontSize: 17, color: "#0f172a", lineHeight: 1.3 }}>{job.job_title}</h4>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                        {job.department || "General Department"}
                        {job.job_code && <span style={{ color: "#94a3b8" }}> · {job.job_code}</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "2px 9px", fontSize: 10, fontWeight: 500, background: "#f5f5f5", color: "#6c757d", border: "1px solid #e0e0e0", textTransform: "capitalize" }}>
                          <i className="ti ti-clock me-1" style={{ fontSize: 10 }} />
                          {job.employment_type ? job.employment_type.replace(/_/g, " ") : "N/A"}
                        </span>
                        <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "2px 9px", fontSize: 10, fontWeight: 500, background: "#f0fdf4", color: "#16a34a", border: "1px solid #dcfce7", textTransform: "capitalize" }}>
                          <i className="ti ti-building me-1" style={{ fontSize: 10 }} />
                          {job.work_mode ? job.work_mode.replace(/_/g, " ") : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  
                  <div className="d-flex align-items-center flex-grow-1 overflow-auto" style={{ padding: "0 0 0 0" }}>
                    <StatTile icon="ti-users" label="Openings" value={job.openings_count ?? "—"} accent="#4f46e5" />
                    <StatTile
                      icon="ti-briefcase"
                      label="Experience"
                      value={job.min_experience_years != null ? `${job.min_experience_years} – ${job.max_experience_years} yrs` : "Any"}
                      accent="#0ea5e9"
                    />
                    <StatTile icon="ti-currency-rupee" label="Salary" value={salaryDisplay} accent="#10b981" />
                    <StatTile icon="ti-map-pin" label="Location" value={job.work_location || "—"} accent="#ef4444" />
                    <StatTile icon="ti-files" label="Applications" value={job.application_count ?? 0} accent="#f59e0b" />
                  </div>
                </div>

                <div style={{ height: 1, background: "#f1f5f9", margin: "16px 0 14px" }} />

                
                <div className="d-flex align-items-center gap-4 flex-wrap">
                  {requiredSkills.length > 0 && (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#b0bec5", textTransform: "uppercase", letterSpacing: "0.5px" }}>Required Skills</span>
                      {requiredSkills.map((s) => <SkillBadge key={s} skill={s} type="required" />)}
                    </div>
                  )}
                  {requiredSkills.length > 0 && preferredSkills.length > 0 && (
                    <div className="flex-shrink-0" style={{ width: 1, height: 28, background: "#e2e8f0" }} />
                  )}
                  {preferredSkills.length > 0 && (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#b0bec5", textTransform: "uppercase", letterSpacing: "0.5px" }}>Preferred Skills</span>
                      {preferredSkills.map((s) => <SkillBadge key={s} skill={s} type="preferred" />)}
                    </div>
                  )}
                  <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>
                      <i className="ti ti-calendar me-1" />Posted {humanReadableDate(job.created_at)}
                    </span>
                    {job.published_at && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        · Published {humanReadableDate(job.published_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SectionPanel>

          
          <SectionPanel
            id="description"
            icon="ti-file-text"
            title="Description & Requirements"
            subtitle="Job summary, responsibilities, qualifications, and additional details"
            accentColor="#0ea5e9"
            defaultOpen
          >
            <Tabs
              defaultActiveKey="summary"
              className="job-detail-tabs"
              tabBarStyle={{ padding: "0 20px", marginBottom: 0, background: "#fafbfc", borderBottom: "1px solid #e9ecef" }}
              items={[
                {
                  key: "summary",
                  label: <span style={{ fontSize: 13, fontWeight: 500 }}><i className="ti ti-align-left me-2" style={{ fontSize: 14 }} />Job Summary</span>,
                  children: (
                    <div className="p-4" style={{ background: "#fafbfc" }}>
                      <div className="row g-4">
                        
                        <div className="col-12 col-xl-8">
                          <div className="p-4 rounded-3 border bg-white shadow-sm">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                              <i className="ti ti-file-description" style={{ color: "#0ea5e9" }} />About this Role
                            </h6>
                            <div
                              className="text-dark"
                              style={{ fontSize: 14, lineHeight: 1.75, color: "#334155" }}
                              dangerouslySetInnerHTML={{ __html: job.detailed_description || job.job_summary || job.description || "No description provided." }}
                            />
                          </div>
                        </div>

                        
                        <div className="col-12 col-xl-4">
                          <div className="p-4 rounded-3 border bg-white shadow-sm mb-4">
                            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                              <i className="ti ti-info-circle" style={{ color: "#4f46e5" }} />Job Details
                            </h6>
                            <div className="d-flex flex-column gap-4">
                              <DetailRow icon="ti-building" label="Department" value={job.department || "—"} accent="#4f46e5" />
                              <DetailRow icon="ti-school" label="Min. Education" value={toTitleCase(job.minimum_education || "")} accent="#0ea5e9" />
                              <DetailRow
                                icon="ti-briefcase"
                                label="Experience"
                                value={job.min_experience_years != null ? `${job.min_experience_years} – ${job.max_experience_years} years` : "Any"}
                                accent="#10b981"
                              />
                              <DetailRow
                                icon="ti-clock"
                                label="Notice Period"
                                value={job.max_notice_period_days ? `Max ${job.max_notice_period_days} Days` : "Negotiable"}
                                accent="#f59e0b"
                              />
                              <DetailRow
                                icon="ti-calendar-x"
                                label="Application Deadline"
                                value={
                                  job.application_deadline
                                    ? <span style={{ color: isDeadlinePassed ? "#ef4444" : daysUntilDeadline != null && daysUntilDeadline <= 3 ? "#f59e0b" : "#334155" }}>
                                      {humanReadableDate(job.application_deadline)}
                                      {daysUntilDeadline != null && !isDeadlinePassed && (
                                        <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.7 }}>({daysUntilDeadline}d left)</span>
                                      )}
                                      {isDeadlinePassed && <span style={{ fontSize: 11, marginLeft: 6 }}>(Closed)</span>}
                                    </span>
                                    : "Not specified"
                                }
                                accent="#ef4444"
                              />
                              <DetailRow
                                icon="ti-currency-rupee"
                                label="Salary"
                                value={salaryDisplay}
                                accent="#10b981"
                              />
                            </div>
                          </div>

                          
                          <div className="p-4 rounded-3 border bg-white shadow-sm">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                              <i className="ti ti-world-upload" style={{ color: "#8b5cf6" }} />Published On
                            </h6>
                            {platforms.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {platforms.map((p: any, i: number) => (
                                  <div key={i} className="d-flex align-items-center justify-content-between p-2 rounded-2" style={{ background: p.is_published ? "#f0fdf4" : "#fff7ed", border: `1px solid ${p.is_published ? "#dcfce7" : "#fed7aa"}` }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <i className={`ti ${p.is_published ? "ti-circle-check" : "ti-clock"}`} style={{ fontSize: 14, color: p.is_published ? "#16a34a" : "#f59e0b" }} />
                                      <span style={{ fontSize: 12, fontWeight: 600, color: "#334155", textTransform: "capitalize" }}>
                                        {(p.platform_name || p.platform || "Unknown").replace(/_/g, " ")}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ fontSize: 10, fontWeight: 600, borderRadius: 999, padding: "1px 7px", background: p.is_published ? "#dcfce7" : "#fed7aa", color: p.is_published ? "#16a34a" : "#d97706" }}>
                                        {p.is_published ? "Published" : "Pending"}
                                      </span>
                                      {p.post_url && (
                                        <a href={p.post_url} target="_blank" rel="noopener noreferrer" style={{ color: "#4f46e5" }} title="View post">
                                          <i className="ti ti-external-link" style={{ fontSize: 13 }} />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ fontSize: 13, color: "#94a3b8" }}>Not published to any platforms yet.</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                },

                {
                  key: "responsibilities",
                  label: <span style={{ fontSize: 13, fontWeight: 500 }}><i className="ti ti-list-check me-2" style={{ fontSize: 14 }} />Responsibilities</span>,
                  children: (
                    <div className="p-4" style={{ background: "#fafbfc" }}>
                      <div className="p-4 rounded-3 border bg-white shadow-sm">
                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                          <i className="ti ti-list-check" style={{ color: "#10b981" }} />Key Responsibilities
                        </h6>
                        {Array.isArray(job.responsibilities) ? (
                          <div className="d-flex flex-column gap-3">
                            {job.responsibilities.map((r: string, i: number) => (
                              <div key={i} className="p-3 rounded-3 border" style={{ background: "#fafbfc", borderLeft: "3px solid #10b981" }}>
                                <div
                                  className="text-dark"
                                  style={{ fontSize: 14, lineHeight: 1.7 }}
                                  dangerouslySetInnerHTML={{ __html: r }}
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div
                            className="text-dark"
                            style={{ fontSize: 14, lineHeight: 1.75 }}
                            dangerouslySetInnerHTML={{ __html: job.responsibilities || "No responsibilities listed." }}
                          />
                        )}
                      </div>
                    </div>
                  ),
                },

                {
                  key: "skills",
                  label: (
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      <i className="ti ti-tools me-2" style={{ fontSize: 14 }} />Skills
                      {(requiredSkills.length + preferredSkills.length) > 0 && (
                        <span className="badge bg-primary rounded-pill ms-1" style={{ fontSize: 10 }}>
                          {requiredSkills.length + preferredSkills.length}
                        </span>
                      )}
                    </span>
                  ),
                  children: (
                    <div className="p-4" style={{ background: "#fafbfc" }}>
                      <div className="row g-4">
                        {requiredSkills.length > 0 && (
                          <div className="col-12 col-md-6">
                            <div className="p-4 rounded-3 border bg-white shadow-sm h-100">
                              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#4f46e5", display: "inline-block" }} />
                                Required Skills
                              </h6>
                              <div className="d-flex flex-wrap gap-2">
                                {requiredSkills.map((s) => <SkillBadge key={s} skill={s} type="required" />)}
                              </div>
                            </div>
                          </div>
                        )}
                        {preferredSkills.length > 0 && (
                          <div className="col-12 col-md-6">
                            <div className="p-4 rounded-3 border bg-white shadow-sm h-100">
                              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#c026d3", display: "inline-block" }} />
                                Preferred Skills
                              </h6>
                              <div className="d-flex flex-wrap gap-2">
                                {preferredSkills.map((s) => <SkillBadge key={s} skill={s} type="preferred" />)}
                              </div>
                            </div>
                          </div>
                        )}
                        {requiredSkills.length === 0 && preferredSkills.length === 0 && (
                          <div className="col-12 text-center py-5 text-muted">
                            <i className="ti ti-tools d-block mb-2 opacity-25" style={{ fontSize: 40 }} />
                            <p style={{ fontSize: 14 }}>No skills specified for this job.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </SectionPanel>

          
          <SectionPanel
            id="screening"
            icon="ti-help-circle"
            title="Screening & Pipeline"
            subtitle="Screening questions and application statistics"
            accentColor="#f59e0b"
            badge={
              screeningQuestions.length > 0 ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 999, padding: "2px 8px", fontSize: 10, fontWeight: 600, background: "#fef9c3", color: "#a16207", border: "1px solid #fde047" }}>
                  <i className="ti ti-help-circle" style={{ fontSize: 10 }} />
                  {screeningQuestions.length} question{screeningQuestions.length !== 1 ? "s" : ""}
                </span>
              ) : undefined
            }
            defaultOpen={false}
          >
            <div className="p-4" style={{ background: "#fafbfc" }}>
              <div className="row g-4">
                
                <div className="col-12">
                  <div className="p-4 rounded-3 border bg-white shadow-sm">
                    <h6 className="fw-bold mb-4 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
                      <i className="ti ti-help-circle" style={{ color: "#f59e0b" }} />Screening Questions
                    </h6>
                    {screeningQuestions.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {screeningQuestions.map((q: any, i: number) => (
                          <div key={i} className="p-4 rounded-3 border" style={{ background: "#fffbeb", borderLeft: "3px solid #f59e0b" }}>
                            <div className="d-flex align-items-start gap-3">
                              <div className="rounded-2 d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0" style={{ width: 26, height: 26, background: "#f59e0b", fontSize: 12 }}>
                                {i + 1}
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                  <p className="fw-semibold text-dark mb-0" style={{ fontSize: 14 }}>{q.question}</p>
                                  {q.required && (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: "#ef4444", background: "#fee2e2", borderRadius: 999, padding: "1px 7px", border: "1px solid #fecaca" }}>Required</span>
                                  )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, textTransform: "capitalize" }}>
                                    <i className="ti ti-input-check me-1" style={{ fontSize: 11 }} />
                                    {(q.type || "text").replace(/_/g, " ")}
                                  </span>
                                  {q.options?.length > 0 && (
                                    <div className="d-flex gap-1 flex-wrap ms-2">
                                      {q.options.map((opt: string, oi: number) => (
                                        <span key={oi} style={{ fontSize: 11, padding: "1px 8px", background: "#f0f4ff", color: "#4f46e5", border: "1px solid #dde3ff", borderRadius: 4, fontWeight: 500 }}>{opt}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="ti ti-clipboard-x d-block mb-2 opacity-25" style={{ fontSize: 36 }} />
                        <p style={{ fontSize: 14 }}>No screening questions configured.</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </SectionPanel>

          <SectionPanel
            id="rounds"
            icon="ti-git-branch"
            title="Interview Pipeline & Rounds"
            subtitle="Configure structured evaluation rounds mapping to this job"
            accentColor="#10b981"
            badge={
              job.rounds?.length > 0 && (
                <span className="badge bg-success rounded-pill ms-2" style={{ fontSize: 10 }}>
                  {job.rounds.length}
                </span>
              )
            }
            defaultOpen={!job.rounds || job.rounds.length === 0}
          >
            <div className="p-4" style={{ background: "#fafbfc" }}>
              <DragDropRoundBuilder jobId={Number(id)} />
            </div>
          </SectionPanel>

        </div>
      </div>
    </div>
  );
};

export default JobDetails;